import React from 'react';
import { Apps, AppNavigation as Navigation } from 'core/types/app';
import { useAppAccess, roleGivesAccess } from 'core/data/app-access';
import { AppBar, Tabs, ButtonBase, Button, lighten } from '@mui/material';
import { Container } from 'core/components/layout';
import styled from 'theme/styled';
import Icon from 'components/icon2';
import { NavLinkProps, NavLink } from 'react-router-dom';
import p, { langcode, LocaleTypes } from 'translation';
import useOrganization from 'core/data/organization';

interface TabProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  forceActive?: () => boolean;
}

const NavLinkRef = React.forwardRef<any, NavLinkProps>((props, ref) => (
  <NavLink ref={ref} {...props} />
));

function Tab({ value, icon, label, forceActive }: TabProps): JSX.Element {
  return (
    <NavigationTab>
      <ButtonBase
        component={NavLinkRef}
        to={value}
        className={forceActive && forceActive() ? 'active' : ''}
        exact
      >
        {icon}
        {label}
      </ButtonBase>
    </NavigationTab>
  );
}

interface Props {
  appId: Apps;
  navigation: Navigation[];
}

function buildPath(appId: Apps, path: string): string {
  return `/app/${appId}${path}`;
}

function transformLangcode(code: LocaleTypes): string {
  switch (code) {
    case 'nb':
      return 'no-no';
    case 'sv':
      return 'sv';
    case 'de':
      return 'de';
    case 'fr':
      return '';
    case 'pl':
      return '';
    case 'en':
      return ''; // default
    default:
      return '';
  }
}

export default function AppNavigation({
  appId,
  navigation,
}: Props): JSX.Element | null {
  const { [appId]: app } = useAppAccess();
  const [org] = useOrganization();

  if (!roleGivesAccess(app.role)) return null;

  return (
    <AppBar color='default' position='sticky' elevation={3} enableColorOnDark>
      <Container margins={0} direction='row'>
        <SecondaryNavigation>
          <Tabs
            variant='scrollable'
            scrollButtons={false}
            indicatorColor='primary'
            textColor='primary'
            value={false}
          >
            {navigation.map(item => {
              if (!item.hasAccess(app.role, org.id)) return null;

              return (
                <Tab
                  key={`navigation:${buildPath(appId, item.path)}`}
                  value={buildPath(appId, item.path)}
                  icon={item.icon}
                  label={item.title}
                  forceActive={item.forceActive}
                />
              );
            })}
          </Tabs>
        </SecondaryNavigation>
        <Help>
          <Button
            endIcon={<Icon icon='question-circle' />}
            onClick={() => {
              window.open(
                `https://help.komp.family/${transformLangcode(langcode)}`,
                '_blank',
              );
            }}
          >
            {p.t('common.help')}
          </Button>
        </Help>
      </Container>
    </AppBar>
  );
}

const SecondaryNavigation = styled.nav`
  flex: 4;
  max-width: 85%;

  @media (max-width: 650px) {
    max-width: 100%;
  }
`;

const Help = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 650px) {
    display: none;
  }
`;

const NavigationTab = styled('div')`
  a,
  button {
    display: flex;
    padding: 1em 1.4em;
    text-transform: uppercase;
    opacity: 0.6;

    &.active,
    &:hover {
      background: ${({ theme }) => lighten(theme.palette.primary.main, 0.9)};
      color: ${({ theme }) => theme.palette.primary.dark};
      border-bottom: 1px solid ${({ theme }) => theme.palette.primary.dark};
      opacity: 1;

      .colorize {
        fill: ${({ theme }) => theme.palette.primary.dark};
      }
    }

    img,
    svg {
      width: 20px;
      max-height: 20px;
      margin-right: 0.5em;
    }
  }
`;
